// Въведеното в редовете на „Целия списък" (само когато модалът е с `entry`):
// стойностите по ред, фокусът в поле и въпросът за презаписа на вече
// попълнен ред в листа. Правилата са в catalogEntryModel.ts; тук е само
// състоянието и връзката с DOM-а.
//
// Фокусът в поле минава през id-то му (`catalogFieldId`) и requestAnimationFrame
// — редът получава полетата си чак след отметката (рендерът), както
// `focusCell` на листовете.
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useConfirm } from "../confirm/useConfirm";
import {
  catalogFieldId,
  enteredValuesOf,
  overwriteConflictsOf,
  sanitizeEntryInput,
  withEntryField,
  type CatalogEntryConfig,
  type CatalogEntryField,
  type CatalogEntryValues,
} from "./catalogEntryModel";
import type { CatalogItem } from "./catalogPickerModel";
import { catalogOptionId } from "./catalogPickerLayout";

type Options = {
  entry?: CatalogEntryConfig;
  listId: string;
  byId: Map<number, CatalogItem>;
};

export const useCatalogEntries = ({ entry, listId, byId }: Options) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [entries, setEntries] = useState<CatalogEntryValues>(() => new Map());
  // втори Ctrl+Enter, докато въпросът е отворен, не пита отново
  const askingRef = useRef(false);
  const allowExpression = entry?.allowExpression ?? false;

  const setField = useCallback(
    (id: number, field: CatalogEntryField, raw: string) =>
      setEntries((current) => withEntryField(current, id, field, sanitizeEntryInput(raw, allowExpression))),
    [allowExpression],
  );

  const fieldOf = useCallback(
    (id: number, field: CatalogEntryField) =>
      document.getElementById(catalogFieldId(catalogOptionId(listId, id), field)) as HTMLInputElement | null,
    [listId],
  );
  // Курсорът в полето на реда (написаното е маркирано — пише се отгоре)
  const focusField = useCallback(
    (id: number, field: CatalogEntryField = "qty") =>
      requestAnimationFrame(() => {
        const input = fieldOf(id, field);
        input?.focus();
        input?.select();
      }),
    [fieldOf],
  );
  // Фокусът е в поле на този ред (редът ще изчезне — напр. махната отметка)
  const isEditing = (id: number) => {
    const focused = document.activeElement;
    return !!focused && (focused === fieldOf(id, "qty") || focused === fieldOf(id, "price"));
  };

  // Стойностите за листа. Вече попълнен ред в листа се презаписва само след
  // въпрос; „Назад" (или Esc) връща в списъка — null, нищо не се добавя.
  const resolveValues = async (ids: number[]): Promise<CatalogEntryValues | null> => {
    const values = enteredValuesOf(ids, entries);
    const conflicts = overwriteConflictsOf(values, byId);
    if (!conflicts.length) return values;
    if (askingRef.current) return null;
    askingRef.current = true;
    try {
      const confirmed = await confirm({
        intent: "warning",
        title: t("inventoryPage.catOverwriteTitle", { count: conflicts.length }),
        explanation: t("inventoryPage.catOverwriteText"),
        preview: {
          caption: t("inventoryPage.catOverwriteCaption"),
          items: conflicts.map((item) => {
            const unit = item.entryUnit ? ` ${item.entryUnit}` : "";
            const typed = values.get(item.id)?.qty || "—";
            return { key: item.id, label: item.name, value: `${item.filledValue}${unit} → ${typed}${unit}` };
          }),
        },
        confirmLabel: t("inventoryPage.catOverwriteConfirm"),
        cancelLabel: t("inventoryPage.catOverwriteBack"),
      });
      return confirmed ? values : null;
    } finally {
      askingRef.current = false;
    }
  };

  return { entries, setField, focusField, isEditing, resolveValues };
};

export type CatalogEntriesState = ReturnType<typeof useCatalogEntries>;
